// Collaboration Analytics
//
// Port of vitalengine-analytics/app/collaborations/page.tsx with two
// adjustments:
//   - Filter strip uses the same controls as our Analytics page (data-type
//     picker, date-range preset, saved filter, facility filter, Load Data).
//   - Data comes from /api/cache/data instead of an in-memory VitalEngine
//     context. Field names are snake_case under record.data.
//
// All aggregation is client-side useMemo over the loaded records, exactly
// like the source page. ROI joins happen in the browser via matchProcedure
// against the procedure list loaded once on mount.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Papa from 'papaparse';
import { GoogleMap, InfoWindow, MarkerF, useJsApiLoader } from '@react-google-maps/api';
import { buildAddressString, geocodeFacilities } from '../utils/geocodeCache';
import client from '../api/client';
import DataTypeSelector from '../components/DataTypeSelector';
import DateRangePicker from '../components/DateRangePicker';
import FacilityFilter from '../components/FacilityFilter';
import { useCache } from '../hooks/useCache';
import { useCustomerFilter } from '../hooks/useCustomerFilter';
import { useScopeSync } from '../hooks/useScopeSync';
import { matchProcedure } from '../utils/procedureMatcher';
import ProcedureFieldKey from '../components/ProcedureFieldKey';
import { FieldHeader, PROCEDURE_FIELD_DEFS } from '../utils/procedureFieldDefs';
import '../components/ProcedureFieldKey.css';
import { isTestRecord } from '../utils/testDataFilter';
import { safeDateString, dateMs } from '../utils/dateUtils';
import { useCurrentScope } from '../hooks/useCurrentScope';
import { useScope } from '../contexts/ScopeContext';
import ScopeBar from '../components/ScopeBar';
import GroupFacilitiesModal from '../components/GroupFacilitiesModal';
import DirectionToggle from '../components/DirectionToggle';
import {
  rowMatchesFacilityScope,
  loadDirectionPreference,
  saveDirectionPreference,
  directionShortLabel,
  dataTypeHasDirection,
  DIRECTION_OPTIONS,
} from '../utils/referralDirection';
import './CollaborationAnalytics.css';

// Only collaboration-style data types make sense here. Both feed the same
// /api/cache/data endpoint and share field shapes (referral_for, sending /
// receiving facility, etc.).
const COLLAB_DATA_TYPES = [
  { id: 'referrals',   name: 'Referrals',   description: 'Referral collaborations' },
  { id: 'vitalrounds', name: 'VitalRounds', description: 'VitalRounds collaborations' },
];

const TABS = [
  { id: 'summary',   label: 'Summary Data' },
  { id: 'roi',       label: 'ROI Data' },
  { id: 'formatted', label: 'Formatted Data' },
  { id: 'raw',       label: 'Raw Data' },
  { id: 'pivot',     label: 'Pivot Table' },
  { id: 'hub',       label: 'Hub Referrals' },
  { id: 'map',       label: 'Map View' },
];

const PAGE_SIZE_OPTIONS = [50, 100, 200];

// --- field accessors ------------------------------------------------------
// External page reads Salesforce-cased fields. Our cached records put the
// snake_case payload under record.data. These helpers keep the rest of the
// file readable.
const f = (r, k) => (r?.data || r?.Data || {})[k];
const ts = (r) => r?.timestamp || (r?.data || r?.Data || {}).created_date;

// Status considered "completed" / "closed". Mirrors the external page's
// notion of completion-rate KPIs.
const COMPLETED_RX = /complete|closed|accepted|finished/i;

// --- date helpers ---------------------------------------------------------
function calculateDateRange(preset, fallbackStart, fallbackEnd) {
  const end = new Date();
  const start = new Date();
  switch (preset) {
    case 'last_month':     start.setDate(start.getDate() - 30); break;
    case 'last_3_months':  start.setMonth(start.getMonth() - 3); break;
    case 'last_6_months':  start.setMonth(start.getMonth() - 6); break;
    case 'last_year':      start.setFullYear(start.getFullYear() - 1); break;
    case 'last_18_months': start.setMonth(start.getMonth() - 18); break;
    case 'last_2_years':   start.setFullYear(start.getFullYear() - 2); break;
    default: return { start: new Date(fallbackStart), end: new Date(fallbackEnd) };
  }
  return { start, end };
}

function formatDate(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

// 'YYYY-MM-DD' -> 'M/D/YYYY' for the inline date-range hint.
function formatDateShort(yyyymmdd) {
  if (!yyyymmdd) return '';
  const [y, m, d] = yyyymmdd.split('-');
  if (!y || !m || !d) return yyyymmdd;
  return `${parseInt(m, 10)}/${parseInt(d, 10)}/${y}`;
}

function isoWeekNumber(date) {
  // ISO 8601 week number; copied from the external page logic.
  const tmp = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = tmp.getUTCDay() || 7;
  tmp.setUTCDate(tmp.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  return Math.ceil((((tmp - yearStart) / 86400000) + 1) / 7);
}

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

// --- formatters -----------------------------------------------------------
const fmtMoney = (n) => (typeof n === 'number' && Number.isFinite(n))
  ? `$${Math.round(n).toLocaleString()}` : '$0';
const fmtPct = (n, digits = 1) => (typeof n === 'number' && Number.isFinite(n))
  ? `${(n * 100).toFixed(digits)}%` : '0%';
const fmtInt = (n) => (typeof n === 'number' ? n.toLocaleString() : '0');

function CollaborationAnalytics() {
  const cache = useCache();

  // ---- Filter strip state (mirror of Analytics.js) -----------------------
  const [selectedType, setSelectedType] = useState('referrals');
  const [dateRangePreset, setDateRangePreset] = useState('last_3_months');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [facilityHierarchy, setFacilityHierarchy] = useState([]);
  const [selectedFacilityIds, setSelectedFacilityIds] = useState(new Set());
  const [showFacilityFilter, setShowFacilityFilter] = useState(false);

  const [savedFilters, setSavedFilters] = useState([]);
  const [activeFilterId, setActiveFilterId] = useState('');

  const { customers, resolveCustomerFacilityIds, facilityToMasterCustomerId } = useCustomerFilter();
  const [activeCustomerId, setActiveCustomerId] = useState('');

  const [removeTestData, setRemoveTestData] = useState(true);

  // Compact filter strip vs full-form view, mirroring the Customer Data
  // Analysis page. Starts expanded so first-time visitors see the
  // dropdowns; flips to the slim summary strip the moment data is
  // loaded so the analysis itself owns the screen real estate.
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);

  // Read-only "View facilities" peek for the green scope bar.
  const [showScopeFacilities, setShowScopeFacilities] = useState(false);

  // Inbound / outbound / either direction filter for the facility scope.
  // Persisted across reloads so a CS rep who lives in the outbound view
  // doesn't get reset every time. Only meaningful for referrals /
  // vitalrounds / group-referrals; ignored on single-facility data types.
  const [direction, setDirectionState] = useState(loadDirectionPreference);
  const setDirection = (v) => {
    setDirectionState(v);
    saveDirectionPreference(v);
  };
  const directionApplies = dataTypeHasDirection(selectedType);

  // Cross-page scope (drives navigation: scope set on this page sticks
  // across pages until cleared). The Clear button on the green Viewing
  // bar must wipe this too, otherwise navigating to another page would
  // re-load the cleared scope from sessionStorage.
  const { clearScope: clearGlobalScope } = useScope();

  // Single source of truth for "what is the user analyzing" so the
  // page title and the green scope bar stay in sync regardless of
  // which scope source the user used (Customer dropdown, Saved Filter
  // dropdown, or Custom Filter modal).
  const currentScope = useCurrentScope({
    activeCustomerId,
    activeFilterId,
    selectedFacilityIds,
    customers,
    savedFilters,
    setActiveCustomerId,
    setActiveFilterId,
    setSelectedFacilityIds,
    clearGlobalScope,
  });

  // Human label for the date-range preset shown in the compact strip.
  // Mirrors the dropdown options so the summary reads naturally
  // (e.g. "Last 3 Months (2/8/2026 - 5/8/2026)").
  const dateRangeLabel = useMemo(() => {
    const presetNames = {
      last_month: 'Last 30 Days',
      last_3_months: 'Last 3 Months',
      last_6_months: 'Last 6 Months',
      last_year: 'Last 12 Months',
      last_18_months: 'Last 18 Months',
      last_2_years: 'Last 2 Years',
      custom: 'Custom Range',
    };
    const base = presetNames[dateRangePreset] || 'Date Range';
    if (startDate && endDate) {
      return `${base} (${formatDateShort(startDate)} - ${formatDateShort(endDate)})`;
    }
    return base;
  }, [dateRangePreset, startDate, endDate]);

  const selectedTypeLabel = useMemo(() => {
    const t = COLLAB_DATA_TYPES.find(x => x.id === selectedType);
    return t?.name || selectedType;
  }, [selectedType]);

  // ---- ROI / procedures --------------------------------------------------
  const [procedures, setProcedures] = useState([]);

  // ---- Tab + per-tab UI state -------------------------------------------
  const [activeTab, setActiveTab] = useState('summary');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [pivotPeriod, setPivotPeriod] = useState('monthly');
  const [pivotSortField, setPivotSortField] = useState('count');
  const [pivotSortDir, setPivotSortDir] = useState('desc');
  const [roiSortField, setRoiSortField] = useState('medProfit');
  const [roiSortDir, setRoiSortDir] = useState('desc');
  // Formatted-tab sort: defaults to newest first (the order the user
  // visually expects when they open the page). Click any header to
  // toggle / switch column; mirrors the Pivot/ROI tab pattern.
  const [formattedSortField, setFormattedSortField] = useState('date');
  const [formattedSortDir, setFormattedSortDir] = useState('desc');

  // Synchronized horizontal scrollbars on the Raw tab.
  const rawTopRef = useRef(null);
  const rawBottomRef = useRef(null);

  // ---- Bootstrap on mount -----------------------------------------------
  useEffect(() => {
    loadFacilityHierarchy();
    loadSavedFilters();
    loadProcedures();
    const { start, end } = calculateDateRange('last_3_months');
    setStartDate(formatDate(start));
    setEndDate(formatDate(end));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (dateRangePreset !== 'custom') {
      const { start, end } = calculateDateRange(dateRangePreset);
      setStartDate(formatDate(start));
      setEndDate(formatDate(end));
    }
  }, [dateRangePreset]);

  const loadFacilityHierarchy = async () => {
    try {
      const response = await cache.getCachedFacilityHierarchy();
      if (response.success) setFacilityHierarchy(response.hierarchy || []);
    } catch { /* hierarchy may not be cached yet */ }
  };

  const loadSavedFilters = async () => {
    try {
      const response = await cache.getSavedFilters();
      if (response?.filters) setSavedFilters(response.filters);
    } catch { /* filters may not exist yet */ }
  };

  const loadProcedures = async () => {
    try {
      const res = await client.get('/api/procedures');
      setProcedures(res.data?.procedures || []);
    } catch { /* permission gated; ROI tab will show empty */ }
  };

  const applySavedFilter = (filterId) => {
    setActiveFilterId(filterId);
    setActiveCustomerId('');
    if (!filterId) { setSelectedFacilityIds(new Set()); }
    else {
      const sf = savedFilters.find(s => s.id === parseInt(filterId, 10));
      if (sf?.facilityIds) setSelectedFacilityIds(new Set(sf.facilityIds));
    }
    setScopeFromFilter(filterId);
  };

  const applyCustomerFilter = async (customerId) => {
    setActiveCustomerId(customerId);
    setActiveFilterId('');
    if (!customerId) { setSelectedFacilityIds(new Set()); }
    else {
      const ids = await resolveCustomerFacilityIds(customerId);
      setSelectedFacilityIds(new Set(ids));
    }
    setScopeFromCustomer(customerId);
  };

  const loadData = useCallback(async () => {
    if (!selectedType || !startDate || !endDate) {
      setError('Please select data type and date range');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await cache.getCachedData(selectedType, startDate, endDate);
      if (response.success) {
        setData(response.records || []);
        setFiltersCollapsed(true);
      } else {
        setError('Failed to load cached data');
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to fetch cached data. Have you refreshed this data type?');
    } finally {
      setLoading(false);
    }
  }, [selectedType, startDate, endDate, cache]);

  // Sticky scope: see useScopeSync.js. Keeps the global StickyScopeBar in
  // lock-step with this page's customer / filter dropdowns and auto-loads
  // when arriving here from a shortcut on My Customers / My Filters / All
  // Facilities (or from another analysis page).
  const { setScopeFromCustomer, setScopeFromFilter } = useScopeSync({
    customers,
    savedFilters,
    resolveCustomerFacilityIds,
    setSelectedFacilityIds,
    setActiveCustomerId,
    setActiveFilterId,
    loadData,
    ready: Boolean(selectedType && startDate && endDate),
  });

  // ---- Derived: facility ids found in data, name map --------------------
  // sending_* / creating_* / receiving_* are the canonical role names —
  // see REFERRAL-FIELD-NAMING.md. The "from" side of a referral is
  // sending_facility_id (the actual referrer / patient source); the "to"
  // side is receiving_facility_id (the destination).
  const dataFacilityIds = useMemo(() => {
    const ids = new Set();
    data.forEach(r => {
      const recv = f(r, 'receiving_facility_id');
      const send = f(r, 'sending_facility_id');
      if (recv != null) ids.add(Math.round(parseFloat(String(recv))).toString());
      if (send != null) ids.add(Math.round(parseFloat(String(send))).toString());
    });
    return ids;
  }, [data]);

  // Walk the cached SF hierarchy once and key every node by its
  // VitalEngine facility ID (canonical, no decimals). The result
  // gives the Map View tab everything it needs to plot a marker:
  // name + address (for the tooltip) + lat/lng (already populated
  // by Salesforce or our Google back-fill — see backend
  // GeocodingService).
  const facilityCoordsByVeId = useMemo(() => {
    const out = new Map();
    const walk = (node) => {
      // The hierarchy endpoint serializes the VE facility id under the
      // legacy Salesforce-style key `vitalEngine_Facility_ID__c` (see
      // backend/Models/SalesforceModels.cs). Other pages on the site
      // already key off that field — we accept the camelCase variant
      // too defensively in case the wire format ever changes.
      const veIdRaw =
        node?.vitalEngine_Facility_ID__c ??
        node?.vitalEngineFacilityId ??
        node?.VitalEngine_Facility_ID__c ??
        null;
      if (veIdRaw != null && veIdRaw !== '') {
        const canonical = Math.round(parseFloat(String(veIdRaw))).toString();
        if (!out.has(canonical)) {
          const latRaw = node.billingLatitude ?? node.BillingLatitude;
          const lngRaw = node.billingLongitude ?? node.BillingLongitude;
          const lat = latRaw != null && latRaw !== '' ? Number(latRaw) : null;
          const lng = lngRaw != null && lngRaw !== '' ? Number(lngRaw) : null;
          out.set(canonical, {
            veId: canonical,
            name: node.label || node.name || `Facility ${canonical}`,
            lat: Number.isFinite(lat) ? lat : null,
            lng: Number.isFinite(lng) ? lng : null,
            street: node.billingStreet || node.BillingStreet || null,
            city: node.billingCity || node.BillingCity || null,
            state: node.billingState || node.BillingState || null,
            postalCode: node.billingPostalCode || node.BillingPostalCode || null,
            country: node.billingCountry || node.BillingCountry || null,
          });
        }
      }
      (node.children || []).forEach(walk);
    };
    (facilityHierarchy || []).forEach(walk);
    return out;
  }, [facilityHierarchy]);

  // ---- Filter pass: optional test-data strip + facility filter ----------
  // Receiver-side only: this page is "what referrals does the customer
  // receive?" — value flows inbound. A referral whose sending OR creating
  // facility belongs to the customer but whose RECEIVING facility is
  // outside the customer's hierarchy is outbound (e.g. a Grandview provider
  // sent a patient to UAB) and is value to the receiver, not to us. Those
  // get excluded so the totals, KPIs, and shading all reflect the same
  // "inbound to our facilities" frame.
  //
  // Same rule applies whether the facility set came from the Customer
  // dropdown, a Saved Filter, or hand-picked via Custom Filter — the page's
  // analytical premise is the same in all three cases.
  const filteredData = useMemo(() => {
    const facIds = selectedFacilityIds;
    return data.filter(r => {
      if (removeTestData && isTestRecord(r)) return false;
      if (facIds.size === 0) return true;
      // r.data || r.Data shape varies across cache responses - the
      // helper expects the inner payload, not the wrapper.
      const inner = r?.data || r?.Data || r;
      return rowMatchesFacilityScope(inner, selectedType, facIds, direction);
    });
  }, [data, selectedFacilityIds, removeTestData, selectedType, direction]);

  // Reset paging when data shape changes.
  useEffect(() => { setPage(1); }, [filteredData, activeTab, searchTerm, pageSize, formattedSortField, formattedSortDir]);

  // ---- External-referral classification ---------------------------------
  // Each row gets one of:
  //   'internal'         — sending + creating + receiving all in the same hierarchy
  //   'external'         — neither sending nor creating in the receiving facility's
  //                        hierarchy (the referral truly originated outside)
  //   'tracked-external' — sending NOT in hierarchy, but creating IS in
  //                        hierarchy as the receiving facility (a customer's
  //                        own staff keyed in a referral that arrived via fax,
  //                        phone, etc., so it could be tracked in VE)
  //   'unknown'          — receiving facility isn't in any customer hierarchy,
  //                        or the master map hasn't loaded yet
  //
  // External referrals are the most valuable to a customer (genuinely new
  // referral business); tracked-external are valuable as adoption signal
  // (customer is using VE as a system of record beyond direct referrals).
  const classifyRow = useCallback((r) => {
    if (!facilityToMasterCustomerId) return 'unknown';
    const recvId = f(r, 'receiving_facility_id');
    if (recvId == null) return 'unknown';
    const recvKey = String(Math.round(parseFloat(String(recvId))));
    const recvMaster = facilityToMasterCustomerId.get(recvKey);
    if (recvMaster == null) return 'unknown';

    const sendId = f(r, 'sending_facility_id');
    const createId = f(r, 'creating_facility_id');
    const sendMaster = sendId != null
      ? facilityToMasterCustomerId.get(String(Math.round(parseFloat(String(sendId)))))
      : undefined;
    const createMaster = createId != null
      ? facilityToMasterCustomerId.get(String(Math.round(parseFloat(String(createId)))))
      : undefined;

    const sendInside   = sendMaster   === recvMaster;
    const createInside = createMaster === recvMaster;

    if (!sendInside && !createInside) return 'external';
    if (!sendInside && createInside)  return 'tracked-external';
    return 'internal'; // includes the rare sendInside + !createInside edge case
  }, [facilityToMasterCustomerId]);

  // Pre-compute classification for every filtered row. We key on the same
  // identity react renders by (referral_id || index) downstream, but keep
  // it simple here: just an array parallel to filteredData.
  const classifications = useMemo(
    () => filteredData.map(classifyRow),
    [filteredData, classifyRow]
  );

  // ---- Summary aggregations ---------------------------------------------
  // "Top Sending Facilities/Specialties" aggregates on sending_* — the
  // actual referring side, i.e. where the patient came from. The typist
  // (creating_*) is tracked separately in topTypistFacs as an adoption
  // metric: facilities that key referrals in on others' behalf are good
  // targets to convert their senders to direct VE submission.
  const summary = useMemo(() => {
    let completed = 0;
    let external = 0;
    let trackedExternal = 0;
    let internal = 0;
    let unclassified = 0;
    const senderSpecCounts   = new Map();
    const receivingFacCounts = new Map();
    const senderFacCounts    = new Map();
    const typistFacCounts    = new Map();

    for (let i = 0; i < filteredData.length; i++) {
      const r = filteredData[i];
      const status = String(f(r, 'referral_status') || '');
      if (COMPLETED_RX.test(status)) completed += 1;

      const cls = classifications[i];
      if      (cls === 'external')         external += 1;
      else if (cls === 'tracked-external') trackedExternal += 1;
      else if (cls === 'internal')         internal += 1;
      else                                 unclassified += 1;

      const senderSpec = f(r, 'sending_speciality');
      if (senderSpec) senderSpecCounts.set(senderSpec, (senderSpecCounts.get(senderSpec) || 0) + 1);

      const recvFac = f(r, 'receiving_facility');
      if (recvFac) receivingFacCounts.set(recvFac, (receivingFacCounts.get(recvFac) || 0) + 1);

      const senderFac = f(r, 'sending_facility');
      if (senderFac) senderFacCounts.set(senderFac, (senderFacCounts.get(senderFac) || 0) + 1);

      // Typist analytic: only count rows where the typist is actually a
      // different person/facility than the sender (i.e. someone keyed it
      // in on someone else's behalf — usually fax/phone intake).
      const typistId  = f(r, 'creating_user_id');
      const senderId  = f(r, 'sending_user_id');
      const typistFac = f(r, 'creating_facility');
      if (typistFac && typistId && senderId && String(typistId) !== String(senderId)) {
        typistFacCounts.set(typistFac, (typistFacCounts.get(typistFac) || 0) + 1);
      }
    }

    const total = filteredData.length;
    const completionRate = total > 0 ? completed / total : 0;
    // Percentages are over the whole filtered total — including unclassified
    // rows. That keeps the math honest: "20% of all collaborations are
    // external" is a stronger statement than "20% of those we could classify."
    const externalPct        = total > 0 ? external / total        : 0;
    const trackedExternalPct = total > 0 ? trackedExternal / total : 0;

    const topN = (m, n = 10) =>
      Array.from(m.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, n)
        .map(([label, count]) => ({ label, count }));

    return {
      total,
      completed,
      completionRate,
      external,
      externalPct,
      trackedExternal,
      trackedExternalPct,
      internal,
      unclassified,
      classificationReady: facilityToMasterCustomerId != null,
      topSendingSpecs:  topN(senderSpecCounts),
      topReceivingFacs: topN(receivingFacCounts),
      topSendingFacs:   topN(senderFacCounts),
      topTypistFacs:    topN(typistFacCounts),
    };
  }, [filteredData, classifications, facilityToMasterCustomerId]);

  // ---- ROI aggregations -------------------------------------------------
  // Each matched referral contributes parallel Medicare and Private dollar
  // amounts. The procedure database publishes CM/Case, Direct LTV,
  // Halo, and Total Value as absolute USD per payer (see
  // frontend/public/docs/procedures-methodology.md), so we sum them
  // directly rather than recomputing revenue * margin.
  const roi = useMemo(() => {
    const empty = {
      matched: 0, unmatched: 0, matchRate: 0,
      med: { revenue: 0, profit: 0, totalValue: 0 },
      priv: { revenue: 0, profit: 0, totalValue: 0 },
      byProcedure: [],
    };
    if (!procedures.length) return empty;

    let matched = 0;
    let medRevenue = 0, medProfit = 0, medTotal = 0;
    let privRevenue = 0, privProfit = 0, privTotal = 0;
    const byProcedure = new Map();

    for (const r of filteredData) {
      const text = f(r, 'referral_for');
      const m = matchProcedure(text, procedures);
      if (!m) continue;

      matched += 1;
      const mr = m.avgMedicareReimbursement || 0;
      const mp = m.avgMedicareProfitPerCase || 0;
      const mt = m.avgMedicareTotalValue || 0;
      const pr = m.avgPrivateReimbursement || 0;
      const pp = m.avgPrivateProfitPerCase || 0;
      const pt = m.avgPrivateTotalValue || 0;

      medRevenue += mr;  medProfit += mp;  medTotal += mt;
      privRevenue += pr; privProfit += pp; privTotal += pt;

      const key = m.procedureName || 'Unknown';
      const agg = byProcedure.get(key) || {
        procedureName: key,
        serviceLine: m.serviceLine || '',
        count: 0,
        medRevenue: 0, medProfit: 0, medTotal: 0,
        privRevenue: 0, privProfit: 0, privTotal: 0,
        // Captured per-procedure (not summed) so the ratio column reflects
        // the underlying per-case ratio rather than a quotient of sums.
        ratio: m.privateToMedicareRatio || (mr > 0 ? pr / mr : null),
      };
      agg.count += 1;
      agg.medRevenue += mr;   agg.medProfit += mp;   agg.medTotal += mt;
      agg.privRevenue += pr;  agg.privProfit += pp;  agg.privTotal += pt;
      byProcedure.set(key, agg);
    }

    const total = filteredData.length;
    const unmatched = total - matched;
    const matchRate = total > 0 ? matched / total : 0;

    const rows = Array.from(byProcedure.values());

    rows.sort((a, b) => {
      const dir = roiSortDir === 'asc' ? 1 : -1;
      const av = a[roiSortField];
      const bv = b[roiSortField];
      if (typeof av === 'string') return av.localeCompare(bv || '') * dir;
      return ((av || 0) - (bv || 0)) * dir;
    });

    return {
      matched, unmatched, matchRate,
      med:  { revenue: medRevenue,  profit: medProfit,  totalValue: medTotal },
      priv: { revenue: privRevenue, profit: privProfit, totalValue: privTotal },
      byProcedure: rows,
    };
  }, [filteredData, procedures, roiSortField, roiSortDir]);

  // ---- Formatted tab: search + sort + page ------------------------------
  // Sort happens AFTER filter but BEFORE pagination so that toggling a
  // column header re-orders the entire result set, not just the page.
  const formattedRows = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const filtered = !q ? filteredData : filteredData.filter(r => {
      // Search across all three roles (sender, typist, destination) so a
      // user can find a referral by any participant's name or facility.
      const blob = [
        f(r, 'patient_first_name'),
        f(r, 'patient_last_name'),
        f(r, 'referral_for'),
        f(r, 'sending_user_name'),
        f(r, 'sending_facility'),
        f(r, 'sending_speciality'),
        f(r, 'creating_user_name'),
        f(r, 'creating_facility'),
        f(r, 'creating_speciality'),
        f(r, 'receiving_user_name'),
        f(r, 'receiving_facility'),
        f(r, 'receiving_speciality'),
        f(r, 'referral_number'),
        f(r, 'referral_status'),
      ].filter(Boolean).join(' ').toLowerCase();
      return blob.includes(q);
    });

    // Each accessor returns the value used for ordering. For the
    // composite cells (Sending/Creating/Receiving) we sort by the
    // user name when present and fall back to the facility name —
    // matches the visual hierarchy in the cell ("name on top, fac
    // underneath") so the eye reads the column the same way the
    // sort orders it.
    const accessor = (() => {
      switch (formattedSortField) {
        case 'date':       return (r) => dateMs(ts(r));
        case 'patient':    return (r) => `${f(r, 'patient_last_name') || ''} ${f(r, 'patient_first_name') || ''}`.trim().toLowerCase();
        case 'referralFor':return (r) => String(f(r, 'referral_for') || '').toLowerCase();
        case 'sending':    return (r) => String(f(r, 'sending_user_name') || f(r, 'sending_facility') || '').toLowerCase();
        case 'creating':   return (r) => String(f(r, 'creating_user_name') || f(r, 'creating_facility') || '').toLowerCase();
        case 'receiving':  return (r) => String(f(r, 'receiving_user_name') || f(r, 'receiving_facility') || '').toLowerCase();
        case 'status':     return (r) => String(f(r, 'referral_status') || '').toLowerCase();
        default:           return (r) => dateMs(ts(r));
      }
    })();

    const dir = formattedSortDir === 'asc' ? 1 : -1;
    // Copy before sort so we never mutate the upstream filteredData
    // (which would invalidate other useMemos that share its identity).
    const out = filtered.slice();
    out.sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      if (typeof av === 'number' && typeof bv === 'number') {
        return (av - bv) * dir;
      }
      // Empty values always sink to the bottom regardless of direction
      // — sorting "blank" to the top is almost never what the user wants.
      const aEmpty = av == null || av === '';
      const bEmpty = bv == null || bv === '';
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;
      if (bEmpty) return -1;
      return String(av).localeCompare(String(bv)) * dir;
    });
    return out;
  }, [filteredData, searchTerm, formattedSortField, formattedSortDir]);

  const formattedPaged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return formattedRows.slice(start, start + pageSize);
  }, [formattedRows, page, pageSize]);

  // ---- Raw tab: page only -----------------------------------------------
  const rawPaged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, page, pageSize]);

  // The Raw tab's column set is the union of keys across visible records,
  // sorted to keep the order stable. Capped to ~30 columns so the grid
  // stays tractable when records have wildly different shapes.
  const rawColumns = useMemo(() => {
    if (rawPaged.length === 0) return [];
    const seen = new Set();
    rawPaged.forEach(r => {
      Object.keys(r.data || r.Data || {}).forEach(k => seen.add(k));
    });
    const cols = Array.from(seen);
    // Stable, useful first-row ordering. Sender (sending_*) -> destination
    // (receiving_*) -> typist (creating_*). The _api_-prefixed forensic
    // copies of VE's original (mislabeled) fields are deprioritized to the
    // end so they only show up if the cap allows.
    const PRIORITY = [
      'created_date', 'referral_id', 'referral_number', 'referral_status', 'referral_for',
      'patient_first_name', 'patient_last_name', 'patient_dob',
      'sending_user_name', 'sending_speciality', 'sending_facility', 'sending_facility_id',
      'receiving_user_name', 'receiving_speciality', 'receiving_facility', 'receiving_facility_id',
      'creating_user_name', 'creating_speciality', 'creating_facility', 'creating_facility_id',
      'test_referral',
    ];
    const isApiBackup = (k) => k.startsWith('_api_');
    cols.sort((a, b) => {
      const aBackup = isApiBackup(a);
      const bBackup = isApiBackup(b);
      if (aBackup !== bBackup) return aBackup ? 1 : -1;
      const ai = PRIORITY.indexOf(a);
      const bi = PRIORITY.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
    return cols.slice(0, 30);
  }, [rawPaged]);

  // ---- Pivot tab --------------------------------------------------------
  const pivotRows = useMemo(() => {
    const map = new Map();
    for (const r of filteredData) {
      const t = ts(r);
      if (!t) continue;
      const d = new Date(t);
      if (Number.isNaN(d.getTime())) continue;

      let period;
      if (pivotPeriod === 'weekly') period = `Wk ${isoWeekNumber(d)}`;
      else if (pivotPeriod === 'quarterly') period = `Q${Math.floor(d.getMonth() / 3) + 1}`;
      else period = MONTH_NAMES[d.getMonth()];

      const year = d.getFullYear();
      // Sending side = the actual sender (sending_*) — see REFERRAL-FIELD-NAMING.md.
      const sendFac  = f(r, 'sending_facility')  || '';
      const recvFac  = f(r, 'receiving_facility') || '';
      const sendProv = f(r, 'sending_user_name') || '';
      const recvProv = f(r, 'receiving_user_name') || '';

      const key = `${sendFac}||${recvFac}||${sendProv}||${recvProv}||${period}||${year}`;
      const agg = map.get(key) || {
        sendingFacility: sendFac,
        receivingFacility: recvFac,
        sendingProvider: sendProv,
        receivingProvider: recvProv,
        period, year,
        count: 0,
      };
      agg.count += 1;
      map.set(key, agg);
    }
    const rows = Array.from(map.values());
    rows.sort((a, b) => {
      const dir = pivotSortDir === 'asc' ? 1 : -1;
      const av = a[pivotSortField];
      const bv = b[pivotSortField];
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
    return rows;
  }, [filteredData, pivotPeriod, pivotSortField, pivotSortDir]);

  // ---- Map View: hub markers + per-spoke aggregates ---------------------
  // Hubs are the customer's selected facilities (the inbound side).
  // Spokes are aggregated from sending_facility_id values in the
  // filtered dataset that AREN'T in the customer's hub set —
  // grouped by id so each external facility gets one marker sized
  // by referral volume rather than one per row.
  //
  // Only meaningful when there is a hub set; without scope the
  // page wants to show "everything" and there's no clear hub vs
  // spoke distinction. The MapTab itself shows a guidance banner
  // in that case.
  const mapData = useMemo(() => {
    const facIds = selectedFacilityIds;

    const hubs = [];
    const hubsMissingCoords = [];
    facIds.forEach(rawId => {
      const canonical = Math.round(parseFloat(String(rawId))).toString();
      const f = facilityCoordsByVeId.get(canonical);
      if (!f) return;
      if (f.lat != null && f.lng != null) hubs.push(f);
      else hubsMissingCoords.push(f);
    });

    // Aggregate spokes from the data already in scope (filteredData
    // is the inbound-only set per `direction` toggle, so this
    // naturally answers "who is referring INTO our hubs?").
    const spokeAgg = new Map(); // canonical id -> { count, name, recvIds:Set }
    let unscopedSenders = 0;
    let spokesNoCoords = 0;
    for (const r of filteredData) {
      const sendIdRaw = f(r, 'sending_facility_id');
      if (sendIdRaw == null || sendIdRaw === '') { unscopedSenders++; continue; }
      const sendId = Math.round(parseFloat(String(sendIdRaw))).toString();
      // Skip senders that are themselves hubs — those are intra-
      // hierarchy referrals, irrelevant to the spoke view.
      if (facIds.has(sendId) || facIds.has(`${sendId}.0`)) continue;

      const recvIdRaw = f(r, 'receiving_facility_id');
      const recvId = recvIdRaw != null ? Math.round(parseFloat(String(recvIdRaw))).toString() : null;
      const sendName = f(r, 'sending_facility') || `Facility ${sendId}`;

      let row = spokeAgg.get(sendId);
      if (!row) {
        row = { veId: sendId, count: 0, name: sendName, recvIds: new Set() };
        spokeAgg.set(sendId, row);
      }
      row.count += 1;
      if (recvId) row.recvIds.add(recvId);
    }

    const spokes = [];
    const spokesMissingCoords = [];
    spokeAgg.forEach(row => {
      const f = facilityCoordsByVeId.get(row.veId);
      if (!f || f.lat == null || f.lng == null) {
        spokesNoCoords += row.count;
        spokesMissingCoords.push({
          veId: row.veId,
          name: f?.name || row.name,
          count: row.count,
          recvIds: row.recvIds,
          street: f?.street ?? null,
          city: f?.city ?? null,
          state: f?.state ?? null,
          postalCode: f?.postalCode ?? null,
          country: f?.country ?? null,
        });
        return;
      }
      spokes.push({
        ...f,
        // Prefer the cached SF name over the per-record one — SF
        // tends to be the cleanest spelling, and using one source
        // keeps the marker labels consistent across InfoWindows.
        name: f.name || row.name,
        count: row.count,
        recvIds: row.recvIds,
      });
    });
    // Largest spokes drawn last so their bigger markers overlay
    // the smaller ones rather than disappearing under them.
    spokes.sort((a, b) => a.count - b.count);

    return {
      hubs,
      spokes,
      hubsMissingCoords,
      spokesMissingCoords,
      unscopedSenders,
      spokesNoCoords,
      totalRows: filteredData.length,
    };
  }, [filteredData, selectedFacilityIds, facilityCoordsByVeId]);

  // ---- Hub Referrals: receiving facility × YYYY-MM matrix ---------------
  const hub = useMemo(() => {
    const facilities = new Map(); // name -> { name, total, byMonth }
    const months = new Set();

    for (const r of filteredData) {
      const recv = f(r, 'receiving_facility');
      if (!recv) continue;
      const t = ts(r);
      if (!t) continue;
      const d = new Date(t);
      if (Number.isNaN(d.getTime())) continue;
      const month = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      months.add(month);

      const row = facilities.get(recv) || { name: recv, total: 0, byMonth: {} };
      row.total += 1;
      row.byMonth[month] = (row.byMonth[month] || 0) + 1;
      facilities.set(recv, row);
    }

    const sortedMonths = Array.from(months).sort();
    const rows = Array.from(facilities.values()).sort((a, b) => b.total - a.total);

    const monthTotals = {};
    sortedMonths.forEach(m => {
      monthTotals[m] = rows.reduce((s, row) => s + (row.byMonth[m] || 0), 0);
    });
    const grand = rows.reduce((s, row) => s + row.total, 0);

    return { months: sortedMonths, rows, monthTotals, grand };
  }, [filteredData]);

  // ---- Sorting helpers --------------------------------------------------
  const togglePivotSort = (field) => {
    if (pivotSortField === field) {
      setPivotSortDir(pivotSortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setPivotSortField(field);
      setPivotSortDir('desc');
    }
  };
  const toggleRoiSort = (field) => {
    if (roiSortField === field) {
      setRoiSortDir(roiSortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setRoiSortField(field);
      setRoiSortDir('desc');
    }
  };
  const toggleFormattedSort = (field) => {
    if (formattedSortField === field) {
      // Same column: just flip direction.
      setFormattedSortDir(formattedSortDir === 'asc' ? 'desc' : 'asc');
    } else {
      // New column: pick the most useful initial direction. Newest-
      // first feels right for dates; A→Z for everything else (so
      // first click makes it scannable, second click reverses).
      setFormattedSortField(field);
      setFormattedSortDir(field === 'date' ? 'desc' : 'asc');
    }
  };

  // ---- CSV exports ------------------------------------------------------
  const exportCsv = (rows, columns, filenameStem) => {
    const csv = Papa.unparse(rows, columns ? { columns } : undefined);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filenameStem}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Sync top scroll bar to bottom (Raw tab UX from the source page).
  const handleRawScroll = (which) => (e) => {
    const peer = which === 'top' ? rawBottomRef.current : rawTopRef.current;
    if (peer && peer.scrollLeft !== e.target.scrollLeft) {
      peer.scrollLeft = e.target.scrollLeft;
    }
  };

  // ---- Render -----------------------------------------------------------
  return (
    <>
      <div className="page-header">
        <h1>
          Collaboration Analytics
          <span className="page-header-scope">
            <span className="page-header-sep">&mdash;</span>
            <span className="page-header-name">{currentScope.name}</span>
          </span>
        </h1>
      </div>

      {filtersCollapsed && data.length > 0 ? (
        <div className="filter-strip-bar">
          <div className="filter-strip-summary">
            <div className="filter-strip-item">
              <span className="filter-strip-label">Date range</span>
              <span className="filter-strip-value">{dateRangeLabel}</span>
            </div>
            <div className="filter-strip-item">
              <span className="filter-strip-label">Data type</span>
              <span className="filter-strip-value">{selectedTypeLabel}</span>
            </div>
            {directionApplies && currentScope.kind !== 'none' && (
              <div className="filter-strip-item">
                <span className="filter-strip-label">Direction</span>
                <span
                  className="filter-strip-value"
                  title={DIRECTION_OPTIONS.find(o => o.value === direction)?.description || ''}
                >
                  {DIRECTION_OPTIONS.find(o => o.value === direction)?.short || direction}
                </span>
              </div>
            )}
            <div className="filter-strip-item">
              <span className="filter-strip-label">Test data</span>
              <span
                className="filter-strip-value"
                title="When 'Hidden', records flagged as test data (TEST/TESTLEY patient names, VitalEngine/MyHeart facilities, etc.) are filtered out."
              >
                {removeTestData ? 'Hidden' : 'Shown'}
              </span>
            </div>
          </div>
          <div className="filter-strip-actions">
            <button
              type="button"
              className="filter-strip-btn filter-strip-btn-ghost"
              onClick={() => setFiltersCollapsed(false)}
            >
              Edit filters
            </button>
            <button
              type="button"
              className="filter-strip-btn filter-strip-btn-primary"
              onClick={loadData}
              disabled={loading || !selectedType || !startDate || !endDate}
            >
              {loading ? 'Loading...' : 'Reload'}
            </button>
          </div>
        </div>
      ) : (
        <div className="controls-panel">
          <DataTypeSelector
            dataTypes={COLLAB_DATA_TYPES}
            selectedType={selectedType}
            onSelectType={setSelectedType}
            disabled={loading}
          />

          <div className="date-range-selector">
            <label htmlFor="collabDatePreset">Date Range:</label>
            <select
              id="collabDatePreset"
              value={dateRangePreset}
              onChange={(e) => setDateRangePreset(e.target.value)}
              disabled={loading}
              className="date-range-dropdown"
            >
              <option value="last_month">Last 30 Days</option>
              <option value="last_3_months">Last 3 Months</option>
              <option value="last_6_months">Last 6 Months</option>
              <option value="last_year">Last 12 Months</option>
              <option value="last_18_months">Last 18 Months</option>
              <option value="last_2_years">Last 2 Years</option>
              <option value="custom">Custom Range</option>
            </select>
            {startDate && endDate && (
              <span
                className="date-range-hint"
                title="The exact start and end dates this range resolves to. All presets are trailing windows ending today."
              >
                {formatDateShort(startDate)} &ndash; {formatDateShort(endDate)}
              </span>
            )}
          </div>

          {dateRangePreset === 'custom' && (
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
              disabled={loading}
            />
          )}

          {savedFilters.length > 0 && (
            <div className="saved-filter-selector">
              <label htmlFor="collabSavedFilter">Saved Filter:</label>
              <select
                id="collabSavedFilter"
                value={activeFilterId}
                onChange={(e) => applySavedFilter(e.target.value)}
                className="date-range-dropdown"
              >
                <option value="">None (show all)</option>
                {savedFilters.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.facilityIds?.length || 0})
                  </option>
                ))}
              </select>
            </div>
          )}

          {customers.length > 0 && (
            <div className="saved-filter-selector">
              <label htmlFor="collabCustomerFilter">Customer:</label>
              <select
                id="collabCustomerFilter"
                value={activeCustomerId}
                onChange={(e) => applyCustomerFilter(e.target.value)}
                className="date-range-dropdown"
              >
                <option value="">None (show all)</option>
                {customers.map(c => (
                  <option key={c.id} value={c.id}>
                    {c._depth > 0 ? '— ' : ''}{c.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <button className="filter-button" onClick={() => setShowFacilityFilter(true)}>
            Custom Filter
            {selectedFacilityIds.size > 0 && ` (${selectedFacilityIds.size})`}
          </button>

          <label
            className="collab-test-toggle"
            title="Hide records flagged as test data, with patient names like TEST/TESTLEY/John Doe, or sent to/from VitalEngine or MyHeart facilities."
          >
            <input
              type="checkbox"
              checked={removeTestData}
              onChange={(e) => setRemoveTestData(e.target.checked)}
            />
            Remove Test Data
          </label>

          {directionApplies && (
            <div
              className="chart-mode-selector"
              title="Inbound = the customer received it. Outbound = the customer sent it. Any = both directions. Only applies when a facility scope is set."
            >
              <label>Direction:</label>
              <DirectionToggle value={direction} onChange={setDirection} />
            </div>
          )}

          <button
            className="load-button"
            onClick={loadData}
            disabled={loading || !selectedType || !startDate || !endDate}
          >
            {loading ? 'Loading...' : 'Load Data'}
          </button>

          {error && <div className="error-message">{error}</div>}
          {cache.error && <div className="error-message">{cache.error}</div>}

          {data.length > 0 && (
            <div className="data-info">
              <strong>{filteredData.length}</strong> of <strong>{data.length}</strong> records
              {currentScope.kind !== 'none' && directionApplies && (
                <span
                  className="collab-filter-direction"
                  title={DIRECTION_OPTIONS.find(o => o.value === direction)?.description || ''}
                >
                  {' '}({directionShortLabel(direction)})
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Errors stay visible regardless of which view is showing. */}
      {filtersCollapsed && (error || cache.error) && (
        <div className="error-message" style={{ marginBottom: '1rem' }}>
          {error || cache.error}
        </div>
      )}

      {filtersCollapsed && data.length > 0 && (
        <div className="data-info" style={{ marginBottom: '1rem' }}>
          <strong>{filteredData.length}</strong> of <strong>{data.length}</strong> records
          {currentScope.kind !== 'none' && directionApplies && (
            <span
              className="collab-filter-direction"
              title={DIRECTION_OPTIONS.find(o => o.value === direction)?.description || ''}
            >
              {' '}({directionShortLabel(direction)})
            </span>
          )}
        </div>
      )}

      <ScopeBar
        scope={currentScope}
        onViewFacilities={
          currentScope.kind !== 'none' && facilityHierarchy?.length && selectedFacilityIds.size
            ? () => setShowScopeFacilities(true)
            : null
        }
      />

      {showScopeFacilities && currentScope.kind !== 'none' && (
        <GroupFacilitiesModal
          groupName={currentScope.name}
          hierarchy={facilityHierarchy}
          facilityIds={Array.from(selectedFacilityIds)}
          onClose={() => setShowScopeFacilities(false)}
        />
      )}

      {data.length === 0 ? (
        <div className="chart-panel">
          <div className="empty-state">
            <p>No data loaded yet for this view.</p>
            <p>
              Pick a data type and date range above, then press <strong>Load Data</strong>.
              An administrator can populate the local cache from <strong>Admin → Data Sources</strong>.
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="collab-tabs" role="tablist">
            {TABS.map(t => (
              <button
                key={t.id}
                role="tab"
                aria-selected={activeTab === t.id}
                className={`collab-tab ${activeTab === t.id ? 'active' : ''}`}
                onClick={() => setActiveTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>

          {activeTab === 'summary'   && <SummaryTab summary={summary} />}
          {activeTab === 'roi'       && (
            <RoiTab
              roi={roi}
              proceduresLoaded={procedures.length > 0}
              sortField={roiSortField}
              sortDir={roiSortDir}
              onSort={toggleRoiSort}
            />
          )}
          {activeTab === 'formatted' && (
            <FormattedTab
              rows={formattedPaged}
              total={formattedRows.length}
              page={page}
              pageSize={pageSize}
              onPage={setPage}
              onPageSize={setPageSize}
              search={searchTerm}
              onSearch={setSearchTerm}
              procedures={procedures}
              classifyRow={classifyRow}
              sortField={formattedSortField}
              sortDir={formattedSortDir}
              onSort={toggleFormattedSort}
            />
          )}
          {activeTab === 'raw'       && (
            <RawTab
              rows={rawPaged}
              total={filteredData.length}
              columns={rawColumns}
              page={page}
              pageSize={pageSize}
              onPage={setPage}
              onPageSize={setPageSize}
              topRef={rawTopRef}
              bottomRef={rawBottomRef}
              onTopScroll={handleRawScroll('top')}
              onBottomScroll={handleRawScroll('bottom')}
            />
          )}
          {activeTab === 'pivot'     && (
            <PivotTab
              rows={pivotRows}
              period={pivotPeriod}
              onPeriod={setPivotPeriod}
              sortField={pivotSortField}
              sortDir={pivotSortDir}
              onSort={togglePivotSort}
              onExport={() => exportCsv(
                pivotRows.map(r => ({
                  'Sending Facility': r.sendingFacility,
                  'Receiving Facility': r.receivingFacility,
                  'Sending Provider': r.sendingProvider,
                  'Receiving Provider': r.receivingProvider,
                  'Period': r.period,
                  'Year': r.year,
                  'Count': r.count,
                })),
                ['Sending Facility','Receiving Facility','Sending Provider','Receiving Provider','Period','Year','Count'],
                `collaboration_pivot_${pivotPeriod}`
              )}
            />
          )}
          {activeTab === 'map'       && (
            <MapTab
              data={mapData}
              scopeName={currentScope?.name}
              hasScope={selectedFacilityIds.size > 0}
              direction={direction}
            />
          )}
          {activeTab === 'hub'       && (
            <HubTab
              hub={hub}
              onExport={() => {
                const rows = hub.rows.map(row => {
                  const out = { 'Receiving Facility': row.name };
                  hub.months.forEach(m => { out[m] = row.byMonth[m] || 0; });
                  out['Total'] = row.total;
                  return out;
                });
                rows.push({
                  'Receiving Facility': 'GRAND TOTAL',
                  ...Object.fromEntries(hub.months.map(m => [m, hub.monthTotals[m] || 0])),
                  Total: hub.grand,
                });
                exportCsv(rows, ['Receiving Facility', ...hub.months, 'Total'], 'hub_referrals');
              }}
            />
          )}
        </>
      )}

      {showFacilityFilter && (
        <FacilityFilter
          hierarchy={facilityHierarchy}
          availableFacilityIds={dataFacilityIds}
          selectedFacilityIds={selectedFacilityIds}
          onSelectionChange={(ids) => { setSelectedFacilityIds(ids); setActiveFilterId(''); setActiveCustomerId(''); }}
          onClose={() => setShowFacilityFilter(false)}
        />
      )}
    </>
  );
}

// =========================================================================
// Tab subcomponents
// =========================================================================

function KpiTile({ label, value, sub, accent, labelTitle }) {
  return (
    <div className={`collab-kpi ${accent ? `collab-kpi-${accent}` : ''}`}>
      <div className="collab-kpi-label" title={labelTitle}>{label}</div>
      <div className="collab-kpi-value">{value}</div>
      {sub && <div className="collab-kpi-sub">{sub}</div>}
    </div>
  );
}

function BarList({ title, items, color = 'teal' }) {
  const max = items.reduce((m, it) => Math.max(m, it.count), 0) || 1;
  return (
    <div className="collab-barlist">
      <h3 className="collab-barlist-title">{title}</h3>
      {items.length === 0 && <p className="collab-barlist-empty">No data</p>}
      {items.map((it, i) => {
        const pct = (it.count / max) * 100;
        return (
          <div key={`${it.label}-${i}`} className="collab-barlist-row">
            <div className="collab-barlist-label" title={it.label}>{it.label}</div>
            <div className="collab-barlist-track">
              <div className={`collab-barlist-fill collab-bar-${color}`} style={{ width: `${pct}%` }} />
            </div>
            <div className="collab-barlist-count">{fmtInt(it.count)}</div>
          </div>
        );
      })}
    </div>
  );
}

function SummaryTab({ summary }) {
  // Two ways the External/Tracked tiles can be uninformative:
  //   1. The facility-master map hasn't loaded yet → show a "loading" sub.
  //   2. None of the receiving facilities are in any customer hierarchy
  //      (e.g. the user is looking at pure spoke data) → show "n/a" rather
  //      than a misleading "0%" number.
  const noClassifiedRows = summary.total > 0
    && summary.classificationReady
    && summary.external + summary.trackedExternal + summary.internal === 0;

  const externalSub = !summary.classificationReady
    ? 'Loading…'
    : summary.total === 0
      ? '—'
      : noClassifiedRows
        ? 'no rows in any customer hierarchy'
        : `${fmtPct(summary.externalPct)} of Collaborations`;

  const trackedSub = !summary.classificationReady
    ? 'Loading…'
    : summary.total === 0
      ? '—'
      : noClassifiedRows
        ? 'no rows in any customer hierarchy'
        : `${fmtPct(summary.trackedExternalPct)} of Collaborations`;

  return (
    <div className="collab-tab-body">
      <div className="collab-kpi-row">
        <KpiTile label="Total Collaborations" value={fmtInt(summary.total)} accent="blue" />
        <KpiTile label="Completion Rate"      value={fmtPct(summary.completionRate)} accent="green" />
        <KpiTile label="Completed"            value={fmtInt(summary.completed)} accent="purple" />
        <KpiTile label="In Progress"          value={fmtInt(summary.total - summary.completed)} accent="amber" />
      </div>

      <div className="collab-kpi-row">
        <KpiTile
          label="External Referrals"
          value={fmtInt(summary.external)}
          sub={externalSub}
          accent="external"
        />
        <KpiTile
          label="Tracked External"
          value={fmtInt(summary.trackedExternal)}
          sub={trackedSub}
          accent="tracked"
        />
        <KpiTile
          label="Internal Referrals"
          value={fmtInt(summary.internal)}
          sub={summary.classificationReady && summary.total > 0
            ? `${fmtPct(summary.internal / summary.total)} of Collaborations`
            : '—'}
        />
      </div>
      <p className="collab-kpi-caption">
        External referrals — neither the sending nor the creating facility belongs to the receiving facility's
        customer hierarchy — represent genuinely new business and are typically the most valuable to a customer.
        Tracked external referrals arrived via another channel (fax, phone, etc.) but were keyed into VitalEngine
        by the customer's own staff so they could be tracked.
      </p>

      <div className="collab-bar-grid">
        <BarList title="Top Sending Specialties"  items={summary.topSendingSpecs}  color="blue" />
        <BarList title="Top Receiving Facilities" items={summary.topReceivingFacs} color="teal" />
        <BarList title="Top Sending Facilities"   items={summary.topSendingFacs}   color="green" />
      </div>

      {summary.topTypistFacs.length > 0 && (
        <div className="collab-bar-grid">
          <BarList
            title="Top Typist Facilities (referrals keyed on someone else's behalf)"
            items={summary.topTypistFacs}
            color="amber"
          />
        </div>
      )}
    </div>
  );
}

function RoiTab({ roi, proceduresLoaded, sortField, sortDir, onSort }) {
  if (!proceduresLoaded) {
    return (
      <div className="collab-tab-body">
        <div className="collab-warning">
          No procedures in the database yet. Visit
          {' '}<a href="/admin/procedures">Admin → Procedure Database</a>{' '}
          to import a CSV before running ROI analysis.
        </div>
      </div>
    );
  }

  const ind = (field) =>
    sortField === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  // Blended = simple 50/50 average of Medicare and Private. A future
  // refinement is per-customer payer-mix (see plan: "Out of scope"). Until
  // then, the blended row is a useful planning anchor for buyers who don't
  // know their own payer split.
  const blended = {
    revenue:    (roi.med.revenue    + roi.priv.revenue)    / 2,
    profit:     (roi.med.profit     + roi.priv.profit)     / 2,
    totalValue: (roi.med.totalValue + roi.priv.totalValue) / 2,
  };

  return (
    <div className="collab-tab-body">
      <div className="collab-roi-kpi-grid">
        <div className="collab-roi-kpi-row collab-roi-kpi-medicare">
          <div className="collab-roi-kpi-label">Medicare</div>
          <KpiTile label="Reimb" value={fmtMoney(roi.med.revenue)} accent="blue"
                   labelTitle={PROCEDURE_FIELD_DEFS.reimb.tooltip} />
          <KpiTile label="CM" value={fmtMoney(roi.med.profit)} accent="green"
                   labelTitle={PROCEDURE_FIELD_DEFS.cm.tooltip} />
          <KpiTile label="Total Value" value={fmtMoney(roi.med.totalValue)} accent="purple"
                   labelTitle={PROCEDURE_FIELD_DEFS.total.tooltip}
                   sub="CM + Direct LTV + Halo" />
        </div>
        <div className="collab-roi-kpi-row collab-roi-kpi-private">
          <div className="collab-roi-kpi-label">Private</div>
          <KpiTile label="Reimb" value={fmtMoney(roi.priv.revenue)} accent="blue"
                   labelTitle={PROCEDURE_FIELD_DEFS.reimb.tooltip} />
          <KpiTile label="CM" value={fmtMoney(roi.priv.profit)} accent="green"
                   labelTitle={PROCEDURE_FIELD_DEFS.cm.tooltip} />
          <KpiTile label="Total Value" value={fmtMoney(roi.priv.totalValue)} accent="purple"
                   labelTitle={PROCEDURE_FIELD_DEFS.total.tooltip}
                   sub="CM + Direct LTV + Halo" />
        </div>
        <div className="collab-roi-kpi-row collab-roi-kpi-blended">
          <div className="collab-roi-kpi-label">Blended (50/50)</div>
          <KpiTile label="Reimb" value={fmtMoney(blended.revenue)} accent="blue"
                   labelTitle={PROCEDURE_FIELD_DEFS.reimb.tooltip} />
          <KpiTile label="CM" value={fmtMoney(blended.profit)} accent="green"
                   labelTitle={PROCEDURE_FIELD_DEFS.cm.tooltip} />
          <KpiTile label="Total Value" value={fmtMoney(blended.totalValue)} accent="purple"
                   labelTitle={PROCEDURE_FIELD_DEFS.total.tooltip} />
          <KpiTile label="Match Rate" value={fmtPct(roi.matchRate)} accent="amber"
                   sub={`${fmtInt(roi.matched)} of ${fmtInt(roi.matched + roi.unmatched)} referrals`} />
        </div>
      </div>

      <ProcedureFieldKey className="collab-roi-field-key" />

      <div className="collab-table-wrap collab-roi-table-wrap">
        <table className="collab-table collab-roi-table">
          <thead>
            <tr className="collab-roi-group-row">
              <th colSpan={2} />
              <th colSpan={3} className="collab-roi-group collab-roi-group-medicare">Medicare</th>
              <th colSpan={3} className="collab-roi-group collab-roi-group-private">Private</th>
              <th />
            </tr>
            <tr>
              <th className="sortable" onClick={() => onSort('procedureName')}>Procedure{ind('procedureName')}</th>
              <th className="num sortable" onClick={() => onSort('count')}>Count{ind('count')}</th>
              <th className="num sortable collab-roi-cell-medicare" onClick={() => onSort('medRevenue')}><FieldHeader fieldId="reimb" />{ind('medRevenue')}</th>
              <th className="num sortable collab-roi-cell-medicare" onClick={() => onSort('medProfit')}><FieldHeader fieldId="cm" />{ind('medProfit')}</th>
              <th className="num sortable collab-roi-cell-medicare" onClick={() => onSort('medTotal')}><FieldHeader fieldId="total" />{ind('medTotal')}</th>
              <th className="num sortable collab-roi-cell-private" onClick={() => onSort('privRevenue')}><FieldHeader fieldId="reimb" />{ind('privRevenue')}</th>
              <th className="num sortable collab-roi-cell-private" onClick={() => onSort('privProfit')}><FieldHeader fieldId="cm" />{ind('privProfit')}</th>
              <th className="num sortable collab-roi-cell-private" onClick={() => onSort('privTotal')}><FieldHeader fieldId="total" />{ind('privTotal')}</th>
              <th className="num sortable" onClick={() => onSort('ratio')}><FieldHeader fieldId="pmRatio" />{ind('ratio')}</th>
            </tr>
          </thead>
          <tbody>
            {roi.byProcedure.length === 0 && (
              <tr><td colSpan={9} className="collab-empty">No referrals matched a known procedure.</td></tr>
            )}
            {roi.byProcedure.map(row => (
              <tr key={row.procedureName}>
                <td>
                  <div className="collab-roi-proc-name">{row.procedureName}</div>
                  {row.serviceLine && <div className="collab-roi-proc-sl">{row.serviceLine}</div>}
                </td>
                <td className="num">{fmtInt(row.count)}</td>
                <td className="num collab-roi-cell-medicare">{fmtMoney(row.medRevenue)}</td>
                <td className="num collab-roi-cell-medicare">{fmtMoney(row.medProfit)}</td>
                <td className="num collab-roi-cell-medicare">{fmtMoney(row.medTotal)}</td>
                <td className="num collab-roi-cell-private">{fmtMoney(row.privRevenue)}</td>
                <td className="num collab-roi-cell-private">{fmtMoney(row.privProfit)}</td>
                <td className="num collab-roi-cell-private">{fmtMoney(row.privTotal)}</td>
                <td className="num">{row.ratio ? `${row.ratio.toFixed(2)}x` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FormattedTab({ rows, total, page, pageSize, onPage, onPageSize, search, onSearch, procedures, classifyRow, sortField, sortDir, onSort }) {
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  // Match the indicator style used by the Pivot/ROI tabs so all three
  // sortable tables in this page feel identical.
  const ind = (field) => sortField === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
  // Defensive: callers that haven't been migrated to pass `onSort` yet
  // shouldn't crash on a header click. (All current call sites do pass
  // it — this is for future-proofing.)
  const handleSort = (field) => onSort && onSort(field);
  return (
    <div className="collab-tab-body">
      <div className="collab-toolbar">
        <input
          type="search"
          className="collab-search"
          placeholder="Search patient, referral, provider, facility..."
          value={search}
          onChange={(e) => onSearch(e.target.value)}
        />
        <PageSizeSelect value={pageSize} onChange={onPageSize} />
        <span className="collab-count">{fmtInt(total)} records</span>
        <span className="collab-legend" title="Row shading reflects how the referral entered VitalEngine relative to the receiving facility's customer hierarchy.">
          <span className="collab-legend-swatch collab-row-external" />External
          <span className="collab-legend-swatch collab-row-tracked-external" />Tracked external
          <span className="collab-legend-swatch collab-row-internal" />Internal
        </span>
      </div>

      <div className="collab-table-wrap">
        <table className="collab-table collab-formatted">
          <thead>
            <tr>
              <th className="sortable" onClick={() => handleSort('date')}>Date{ind('date')}</th>
              <th className="sortable" onClick={() => handleSort('patient')}>Patient{ind('patient')}</th>
              <th className="sortable" onClick={() => handleSort('referralFor')}>Referral For{ind('referralFor')}</th>
              <th className="sortable" onClick={() => handleSort('sending')}>Sending{ind('sending')}</th>
              <th className="sortable" onClick={() => handleSort('creating')}>Creating{ind('creating')}</th>
              <th className="sortable" onClick={() => handleSort('receiving')}>Receiving{ind('receiving')}</th>
              <th className="sortable" onClick={() => handleSort('status')}>Status{ind('status')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={7} className="collab-empty">No records.</td></tr>
            )}
            {rows.map((r, i) => {
              // safeDateString tolerates the Salesforce date-object shape
              // ({ year, month, day, value, ... }) that occasionally slips
              // through unnormalized — rendering it raw throws React #31.
              const date = safeDateString(ts(r)) || '—';
              const fn = f(r, 'patient_first_name') || '';
              const ln = f(r, 'patient_last_name') || '';
              const dob = safeDateString(f(r, 'patient_dob'));
              const referralFor = f(r, 'referral_for') || '';
              const matched = referralFor ? matchProcedure(referralFor, procedures) : null;

              // sending_* / creating_* / receiving_* are the canonical role names
              // produced by DataCacheService. See REFERRAL-FIELD-NAMING.md.
              const sendUser = f(r, 'sending_user_name');
              const sendSpec = f(r, 'sending_speciality');
              const sendFac  = f(r, 'sending_facility');
              const hasSend  = sendUser || sendSpec || sendFac;

              // Always show typist data when present (matches VE's spreadsheet —
              // their Created By column is populated for every row, even when
              // the typist is the same person as the sender).
              const createUser = f(r, 'creating_user_name');
              const createSpec = f(r, 'creating_speciality');
              const createFac  = f(r, 'creating_facility');
              const hasCreate  = createUser || createFac;

              const cls = classifyRow ? classifyRow(r) : 'unknown';
              const rowClass =
                cls === 'external'         ? 'collab-row-external' :
                cls === 'tracked-external' ? 'collab-row-tracked-external' :
                '';
              const rowTitle =
                cls === 'external'         ? 'External referral — both sender and creator are outside this customer hierarchy' :
                cls === 'tracked-external' ? 'Tracked external — sender is outside the hierarchy but a customer user keyed it in (e.g. fax/phone intake tracked in VE)' :
                cls === 'internal'         ? 'Internal — all three facilities (sending, creating, receiving) belong to the same customer hierarchy' :
                undefined;
              return (
                <tr key={`${f(r, 'referral_id') || i}-${i}`} className={rowClass} title={rowTitle}>
                  <td className="collab-cell-date">{date}</td>
                  <td>
                    <div className="collab-patient-name">{fn} {ln}</div>
                    {dob && <div className="collab-patient-dob">DOB: {dob}</div>}
                  </td>
                  <td>
                    <span className={matched ? 'collab-pill collab-pill-match' : ''} title={matched?.procedureName}>
                      {referralFor || '—'}
                    </span>
                  </td>
                  <td>
                    {hasSend ? (
                      <>
                        <div className="collab-prov-name">{sendUser || '—'}</div>
                        <div className="collab-prov-meta">{sendSpec || ''}</div>
                        <div className="collab-prov-meta">{sendFac  || ''}</div>
                      </>
                    ) : '—'}
                  </td>
                  <td>
                    {hasCreate ? (
                      <>
                        <div className="collab-prov-name">{createUser || '—'}</div>
                        <div className="collab-prov-meta">{createSpec || ''}</div>
                        <div className="collab-prov-meta">{createFac  || ''}</div>
                      </>
                    ) : '—'}
                  </td>
                  <td>
                    <div className="collab-prov-name">{f(r, 'receiving_user_name') || '—'}</div>
                    <div className="collab-prov-meta">{f(r, 'receiving_speciality') || ''}</div>
                    <div className="collab-prov-meta">{f(r, 'receiving_facility') || ''}</div>
                  </td>
                  <td><StatusBadge status={f(r, 'referral_status')} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pager page={page} lastPage={lastPage} onPage={onPage} />
    </div>
  );
}

function StatusBadge({ status }) {
  if (!status) return <span className="collab-status">—</span>;
  const s = String(status);
  const cls = COMPLETED_RX.test(s) ? 'collab-status collab-status-good'
    : /pending|new|open|in[ _-]?progress/i.test(s) ? 'collab-status collab-status-pending'
    : /declin|reject|cancel/i.test(s) ? 'collab-status collab-status-bad'
    : 'collab-status';
  return <span className={cls}>{s}</span>;
}

function RawTab({ rows, total, columns, page, pageSize, onPage, onPageSize, topRef, bottomRef, onTopScroll, onBottomScroll }) {
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  // Approximate width so the top scrollbar maps to the bottom one. 180px /
  // column matches the source's default table sizing for the wide grid.
  const innerWidth = Math.max(900, columns.length * 180);

  return (
    <div className="collab-tab-body">
      <div className="collab-toolbar">
        <PageSizeSelect value={pageSize} onChange={onPageSize} />
        <span className="collab-count">{fmtInt(total)} records</span>
      </div>

      <div ref={topRef} onScroll={onTopScroll} className="collab-raw-scroll-top">
        <div style={{ width: innerWidth, height: 1 }} />
      </div>

      <div ref={bottomRef} onScroll={onBottomScroll} className="collab-raw-scroll-bottom">
        <table className="collab-table collab-raw" style={{ minWidth: innerWidth }}>
          <thead>
            <tr>
              {columns.map(c => <th key={c}>{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={columns.length} className="collab-empty">No records.</td></tr>
            )}
            {rows.map((r, i) => (
              <tr key={i}>
                {columns.map(c => {
                  const v = (r.data || r.Data || {})[c];
                  return <td key={c}>{v === null || v === undefined || v === '' ? '—' : String(v)}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pager page={page} lastPage={lastPage} onPage={onPage} />
    </div>
  );
}

function PivotTab({ rows, period, onPeriod, sortField, sortDir, onSort, onExport }) {
  const sortIndicator = (field) =>
    sortField === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
  return (
    <div className="collab-tab-body">
      <div className="collab-toolbar">
        <div className="collab-segmented">
          {['weekly','monthly','quarterly'].map(p => (
            <button
              key={p}
              className={`collab-segmented-btn ${period === p ? 'active' : ''}`}
              onClick={() => onPeriod(p)}
            >
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </button>
          ))}
        </div>
        <span className="collab-count">{fmtInt(rows.length)} pivot rows</span>
        <button className="collab-export-btn" onClick={onExport} disabled={rows.length === 0}>
          Export CSV
        </button>
      </div>

      <div className="collab-table-wrap">
        <table className="collab-table collab-pivot">
          <thead>
            <tr>
              <th className="sortable" onClick={() => onSort('sendingFacility')}>Sending Facility{sortIndicator('sendingFacility')}</th>
              <th className="sortable" onClick={() => onSort('receivingFacility')}>Receiving Facility{sortIndicator('receivingFacility')}</th>
              <th className="sortable" onClick={() => onSort('sendingProvider')}>Sending Provider{sortIndicator('sendingProvider')}</th>
              <th className="sortable" onClick={() => onSort('receivingProvider')}>Receiving Provider{sortIndicator('receivingProvider')}</th>
              <th className="sortable" onClick={() => onSort('period')}>Period{sortIndicator('period')}</th>
              <th className="num sortable" onClick={() => onSort('year')}>Year{sortIndicator('year')}</th>
              <th className="num sortable" onClick={() => onSort('count')}>Count{sortIndicator('count')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={7} className="collab-empty">No pivot data.</td></tr>
            )}
            {rows.map((r, i) => (
              <tr key={i}>
                <td>{r.sendingFacility || '—'}</td>
                <td>{r.receivingFacility || '—'}</td>
                <td>{r.sendingProvider || '—'}</td>
                <td>{r.receivingProvider || '—'}</td>
                <td>{r.period}</td>
                <td className="num">{r.year}</td>
                <td className="num">{fmtInt(r.count)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function HubTab({ hub, onExport }) {
  return (
    <div className="collab-tab-body">
      <div className="collab-toolbar">
        <span className="collab-count">{fmtInt(hub.rows.length)} receiving facilities × {fmtInt(hub.months.length)} months</span>
        <button className="collab-export-btn" onClick={onExport} disabled={hub.rows.length === 0}>
          Export CSV
        </button>
      </div>

      <div className="collab-table-wrap">
        <table className="collab-table collab-hub">
          <thead>
            <tr>
              <th>Receiving Facility</th>
              {hub.months.map(m => <th key={m} className="num">{m}</th>)}
              <th className="num">Total</th>
            </tr>
          </thead>
          <tbody>
            {hub.rows.length === 0 && (
              <tr><td colSpan={hub.months.length + 2} className="collab-empty">No data.</td></tr>
            )}
            {hub.rows.map(row => (
              <tr key={row.name}>
                <td>{row.name}</td>
                {hub.months.map(m => (
                  <td key={m} className="num">{row.byMonth[m] ? fmtInt(row.byMonth[m]) : '—'}</td>
                ))}
                <td className="num bold">{fmtInt(row.total)}</td>
              </tr>
            ))}
            {hub.rows.length > 0 && (
              <tr className="collab-grand-row">
                <td>GRAND TOTAL</td>
                {hub.months.map(m => (
                  <td key={m} className="num bold">{fmtInt(hub.monthTotals[m] || 0)}</td>
                ))}
                <td className="num bold">{fmtInt(hub.grand)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PageSizeSelect({ value, onChange }) {
  return (
    <select
      className="collab-pagesize"
      value={value}
      onChange={(e) => onChange(parseInt(e.target.value, 10))}
    >
      {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n} / page</option>)}
    </select>
  );
}

function Pager({ page, lastPage, onPage }) {
  if (lastPage <= 1) return null;
  return (
    <div className="collab-pager">
      <button onClick={() => onPage(1)}                 disabled={page <= 1}>« First</button>
      <button onClick={() => onPage(page - 1)}          disabled={page <= 1}>‹ Prev</button>
      <span>Page {page} of {lastPage}</span>
      <button onClick={() => onPage(page + 1)}          disabled={page >= lastPage}>Next ›</button>
      <button onClick={() => onPage(lastPage)}          disabled={page >= lastPage}>Last »</button>
    </div>
  );
}

// =========================================================================
// Map View tab
// =========================================================================
//
// Plots two layers of markers:
//   - HUBS  (the customer's selected facilities) as large blue dots so
//           they stand out against the spoke noise.
//   - SPOKES (every external sending_facility_id from filteredData)
//           as red dots whose radius scales by referral count.
//
// Reuses the same Google Maps loader as FacilityMapModal so the JS SDK
// only loads once across the app. Self-fits bounds whenever the marker
// set changes (changing date range, scope, or direction toggle all
// trigger a re-fit).

const COLLAB_MAP_LIBRARIES = ['places'];
const COLLAB_MAP_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
const COLLAB_MAP_CONTAINER = { width: '100%', height: '600px' };
const COLLAB_DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 };
const COLLAB_DEFAULT_ZOOM = 4;

// Spoke marker radius (px) based on referral count. Square-root keeps
// the dots from becoming city-sized for the busiest senders while
// still giving a clear visual ranking.
function spokeRadius(count) {
  if (count <= 0) return 5;
  const r = 5 + Math.sqrt(count) * 1.5;
  return Math.min(r, 28);
}

function MapTab({ data, scopeName, hasScope, direction }) {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: COLLAB_MAP_KEY,
    libraries: COLLAB_MAP_LIBRARIES,
  });

  const [activeId, setActiveId] = useState(null);
  const [activeLayer, setActiveLayer] = useState(null); // 'hub' | 'spoke'
  const mapRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const [resolvedCoords, setResolvedCoords] = useState({});
  const [geocoding, setGeocoding] = useState(false);

  // Facilities that need client-side geocoding (prod often lacks cached
  // lat/lng because backend GeocodingService needs GOOGLE_MAPS_API_KEY).
  const needGeocode = useMemo(() => {
    const seen = new Set();
    const list = [];
    for (const f of [...data.hubsMissingCoords, ...(data.spokesMissingCoords || [])]) {
      if (!f?.veId || seen.has(f.veId)) continue;
      const addr = buildAddressString(f);
      if (!addr) continue;
      seen.add(f.veId);
      list.push({ ...f, _address: addr });
    }
    return list;
  }, [data.hubsMissingCoords, data.spokesMissingCoords]);

  const needGeocodeKey = useMemo(
    () => needGeocode.map(f => `${f.veId}|${f._address}`).join('||'),
    [needGeocode]
  );

  useEffect(() => {
    if (!isLoaded || !needGeocodeKey) return;
    let cancelled = false;
    setGeocoding(true);
    (async () => {
      const resolved = await geocodeFacilities(needGeocode);
      if (!cancelled && Object.keys(resolved).length > 0) {
        setResolvedCoords(prev => ({ ...prev, ...resolved }));
      }
      if (!cancelled) setGeocoding(false);
    })();
    return () => { cancelled = true; };
  }, [isLoaded, needGeocodeKey, needGeocode]);

  const displayHubs = useMemo(() => {
    const extra = data.hubsMissingCoords
      .map(f => {
        const c = resolvedCoords[f.veId];
        return c ? { ...f, lat: c.lat, lng: c.lng } : null;
      })
      .filter(Boolean);
    return [...data.hubs, ...extra];
  }, [data.hubs, data.hubsMissingCoords, resolvedCoords]);

  const displaySpokes = useMemo(() => {
    const extra = (data.spokesMissingCoords || [])
      .map(f => {
        const c = resolvedCoords[f.veId];
        return c ? { ...f, lat: c.lat, lng: c.lng, name: f.name } : null;
      })
      .filter(Boolean);
    return [...data.spokes, ...extra].sort((a, b) => a.count - b.count);
  }, [data.spokes, data.spokesMissingCoords, resolvedCoords]);

  const hubsStillMissing = useMemo(
    () => data.hubsMissingCoords.filter(f => !resolvedCoords[f.veId]).length,
    [data.hubsMissingCoords, resolvedCoords]
  );

  const spokesStillMissing = useMemo(() => {
    let n = 0;
    for (const f of data.spokesMissingCoords || []) {
      if (!resolvedCoords[f.veId]) n += f.count || 0;
    }
    return n;
  }, [data.spokesMissingCoords, resolvedCoords]);

  const boundsKey = useMemo(() => {
    const h = displayHubs.map(p => `H${p.veId}`).join('|');
    const s = displaySpokes.map(p => `S${p.veId}:${p.count}`).join('|');
    return `${h}#${s}`;
  }, [displayHubs, displaySpokes]);

  useEffect(() => {
    if (!mapReady || !mapRef.current || !window.google?.maps) return;
    const all = [...displayHubs, ...displaySpokes];
    if (all.length === 0) return;
    if (all.length === 1) {
      mapRef.current.setCenter({ lat: all[0].lat, lng: all[0].lng });
      mapRef.current.setZoom(11);
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    all.forEach(p => bounds.extend({ lat: p.lat, lng: p.lng }));
    // 80px padding gives the spoke dots room near the edges of the
    // 600px-tall map without clipping their stroke.
    mapRef.current.fitBounds(bounds, 80);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boundsKey, mapReady]);

  const onMapLoad = (map) => {
    mapRef.current = map;
    setMapReady(true);
  };
  const onMapUnmount = () => {
    mapRef.current = null;
    setMapReady(false);
  };
  const closeInfo = () => { setActiveId(null); setActiveLayer(null); };

  // Marker icon factories — depend on `isLoaded` because they reference
  // window.google.maps.SymbolPath. Returning plain objects from the
  // body would crash before the SDK is ready.
  const hubIcon = useMemo(() => {
    if (!isLoaded || !window.google?.maps) return undefined;
    return {
      path: window.google.maps.SymbolPath.CIRCLE,
      scale: 12,
      fillColor: '#0f766e',
      fillOpacity: 0.95,
      strokeColor: '#ffffff',
      strokeWeight: 3,
    };
  }, [isLoaded]);

  const makeSpokeIcon = (count) => {
    if (!isLoaded || !window.google?.maps) return undefined;
    return {
      path: window.google.maps.SymbolPath.CIRCLE,
      scale: spokeRadius(count),
      fillColor: '#dc2626',
      fillOpacity: 0.55,
      strokeColor: '#7f1d1d',
      strokeWeight: 1.5,
    };
  };

  const activeMarker = useMemo(() => {
    if (!activeId || !activeLayer) return null;
    const list = activeLayer === 'hub' ? displayHubs : displaySpokes;
    return list.find(p => String(p.veId) === String(activeId)) || null;
  }, [activeId, activeLayer, displayHubs, displaySpokes]);

  // Empty / guidance states ------------------------------------------------
  if (!COLLAB_MAP_KEY) {
    return (
      <div className="collab-tab-body">
        <div className="collab-empty-block">
          <strong>Map unavailable</strong>
          <span>
            <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> isn't set on this build.
          </span>
        </div>
      </div>
    );
  }

  if (!hasScope) {
    return (
      <div className="collab-tab-body">
        <div className="collab-empty-block">
          <strong>Pick a customer or saved filter to see the hub-and-spoke map.</strong>
          <span>
            The map plots the selected customer's facilities as hubs (large
            teal dots) and every external facility that sent a referral into
            them during the chosen date window as spokes (red dots, sized by
            referral count).
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="collab-tab-body">
      <div className="collab-toolbar collab-map-toolbar">
        <span className="collab-count">
          <span className="collab-map-swatch collab-map-swatch-hub" /> {displayHubs.length} hub{displayHubs.length === 1 ? '' : 's'}
          {' · '}
          <span className="collab-map-swatch collab-map-swatch-spoke" /> {displaySpokes.length} spoke{displaySpokes.length === 1 ? '' : 's'}
          {geocoding && (
            <> {' · '} <em>Geocoding addresses…</em></>
          )}
          {!geocoding && hubsStillMissing > 0 && (
            <> {' · '} <em>{hubsStillMissing} hub{hubsStillMissing === 1 ? '' : 's'} missing coords</em></>
          )}
          {!geocoding && spokesStillMissing > 0 && (
            <> {' · '} <em>{spokesStillMissing.toLocaleString()} referral{spokesStillMissing === 1 ? '' : 's'} from un-geocoded spokes</em></>
          )}
        </span>
        <span className="collab-map-direction-hint">
          {direction === 'inbound'  && 'Inbound only — counting referrals received by hubs'}
          {direction === 'outbound' && 'Outbound only — spokes are receivers of hub-originated referrals'}
          {direction === 'either'   && 'Any direction — spokes touched hubs from either side'}
        </span>
      </div>

      {scopeName && (
        <div className="collab-map-subtitle">
          Hubs: <strong>{scopeName}</strong>
        </div>
      )}

      <div className="collab-map-wrap">
        {loadError && (
          <div className="collab-empty-block">
            <strong>Map failed to load</strong>
            <span>Make sure the Maps JavaScript API is enabled on the Google Cloud project that owns the API key.</span>
          </div>
        )}
        {!loadError && !isLoaded && (
          <div className="collab-empty-block">Loading map…</div>
        )}
        {!loadError && isLoaded && (
          <GoogleMap
            mapContainerStyle={COLLAB_MAP_CONTAINER}
            center={COLLAB_DEFAULT_CENTER}
            zoom={COLLAB_DEFAULT_ZOOM}
            onLoad={onMapLoad}
            onUnmount={onMapUnmount}
            options={{
              mapTypeControl: false,
              streetViewControl: false,
              fullscreenControl: true,
            }}
          >
            {/* Spokes are drawn first so the hub markers (rendered after)
                sit visually on top — important when a spoke happens to
                share an address with a hub (e.g. a campus with both
                customer-owned and external clinics). */}
            {displaySpokes.map(p => (
              <MarkerF
                key={`spoke-${p.veId}`}
                position={{ lat: p.lat, lng: p.lng }}
                icon={makeSpokeIcon(p.count)}
                onClick={() => { setActiveId(p.veId); setActiveLayer('spoke'); }}
              />
            ))}
            {displayHubs.map(p => (
              <MarkerF
                key={`hub-${p.veId}`}
                position={{ lat: p.lat, lng: p.lng }}
                icon={hubIcon}
                onClick={() => { setActiveId(p.veId); setActiveLayer('hub'); }}
              />
            ))}
            {activeMarker && (
              <InfoWindow
                position={{ lat: activeMarker.lat, lng: activeMarker.lng }}
                onCloseClick={closeInfo}
              >
                <div className="collab-map-info">
                  <div className={`collab-map-info-tag ${activeLayer === 'hub' ? 'hub' : 'spoke'}`}>
                    {activeLayer === 'hub' ? 'HUB' : 'SPOKE'}
                  </div>
                  <div className="collab-map-info-name">{activeMarker.name}</div>
                  <div className="collab-map-info-addr">
                    {[
                      activeMarker.street,
                      [activeMarker.city, activeMarker.state, activeMarker.postalCode].filter(Boolean).join(', '),
                    ].filter(Boolean).join('\n') || 'No address on file'}
                  </div>
                  {activeLayer === 'spoke' && (
                    <div className="collab-map-info-stat">
                      <strong>{activeMarker.count.toLocaleString()}</strong> referral{activeMarker.count === 1 ? '' : 's'} into hub{activeMarker.recvIds?.size > 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              </InfoWindow>
            )}
          </GoogleMap>
        )}
      </div>

      {data.hubsMissingCoords.length > 0 && (
        <div className="collab-map-warnlist">
          <strong>Hubs missing coordinates ({data.hubsMissingCoords.length})</strong>
          <ul>
            {data.hubsMissingCoords.slice(0, 8).map(h => (
              <li key={h.veId}>{h.name} <span>(VE ID {h.veId})</span></li>
            ))}
            {data.hubsMissingCoords.length > 8 && (
              <li><em>…and {data.hubsMissingCoords.length - 8} more</em></li>
            )}
          </ul>
          <span className="collab-map-warnhint">
            Open <a href="/facilities">All Facilities</a> → toggle <em>Missing
            address only</em> to spot the rows Salesforce needs cleaned up.
          </span>
        </div>
      )}
    </div>
  );
}

export default CollaborationAnalytics;
